import { FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useCreateMatch } from "../hooks";
import { MatchDraft } from "../types";

export const GuestDetailsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const draft = (location.state as { draft?: MatchDraft } | null)?.draft;
  const totalGoing = draft ? 4 - draft.spotsNeeded : 0;
  const companionCount = Math.max(totalGoing - 1, 0);
  const [organizerName, setOrganizerName] = useState(draft?.userName || "");
  const [companionNames, setCompanionNames] = useState<string[]>(
    Array(companionCount).fill("")
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState("");
  const { mutate, isPending } = useCreateMatch();

  if (!draft) {
    return <Navigate to="/crear" replace />;
  }

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    const nextErrors: Record<string, string> = {};

    if (!draft.userName && !organizerName.trim()) {
      nextErrors.organizer = t("guests.nameRequired");
    }

    companionNames.forEach((name, index) => {
      if (!name.trim()) {
        nextErrors[`companion_${index}`] = t("guests.companionRequired");
      }
    });

    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    const names = [draft.userName || organizerName, ...companionNames]
      .map((name) => name.trim())
      .filter(Boolean);

    mutate(
      {
        location: draft.location,
        date: draft.date,
        time: draft.time,
        spotsNeeded: draft.spotsNeeded,
        note: draft.note,
        names,
        userId: draft.userId,
        userEmail: draft.userEmail,
      },
      {
        onSuccess: (match) => {
          navigate("/compartir", { state: { match } });
        },
        onError: () => {
          setSubmitError(t("guests.createError"));
        },
      }
    );
  };

  return (
    <main className="pp-screen">
      <header className="pp-header">
        <Link to="/crear" className="pp-back">
          {t("common.backArrow")}
        </Link>
        <h1>{t("guests.title")}</h1>
        <p>{t("guests.subtitle", { count: totalGoing })}</p>
      </header>

      <form className="pp-card" onSubmit={handleSubmit}>
        <div className="pp-field">
          <label htmlFor="organizer">{t("guests.yourName")}</label>
          <input
            id="organizer"
            placeholder={t("common.exampleName")}
            value={organizerName}
            disabled={Boolean(draft.userName)}
            onChange={(event) => setOrganizerName(event.target.value)}
          />
          {errors.organizer && <p className="pp-error">{errors.organizer}</p>}
        </div>

        {companionCount > 0 && (
          <>
            <p className="pp-legend">{t("guests.companions")}</p>
            {companionNames.map((name, index) => (
              <div className="pp-field" key={index}>
                <label htmlFor={`companion-${index}`}>
                  {t("guests.companion", { index: index + 1 })}
                </label>
                <input
                  id={`companion-${index}`}
                  placeholder={t("common.exampleCompanion")}
                  value={name}
                  onChange={(event) => {
                    const next = [...companionNames];
                    next[index] = event.target.value;
                    setCompanionNames(next);
                  }}
                />
                {errors[`companion_${index}`] && (
                  <p className="pp-error">{errors[`companion_${index}`]}</p>
                )}
              </div>
            ))}
          </>
        )}

        {submitError && <p className="pp-error">{submitError}</p>}

        <button type="submit" className="pp-btn" disabled={isPending}>
          {isPending ? t("create.creating") : t("create.title")}
        </button>
      </form>
    </main>
  );
};
